// The right-hand list of everything that happened: filter by state (leads)
// and kind, sort by time or severity, thumbnails, triage for leads. The
// card whose span contains the playhead lights up and scrolls into view,
// the same way CallDetail's flag rail follows the transcript.
import { useEffect, useMemo, useRef, useState } from "react";
import { ClockIcon, FireIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Dropdown from "../../Settings/Dropdown";
import Tooltip from "../Tooltip";
import { revealProps } from "../Reveal";
import TriageButton from "../../Investigations/TriageButton";
import { DISMISS_REASONS, SEVERITY_ACTIVE_CLASSES, SEVERITY_CLASSES } from "../../Investigations/api";
import { VideoPoint, formatClock, kindLabel, kindStyle, pointsAt } from "./types";
import { scrollWithin } from "../../../utilities/scrollWithin";

interface Props {
  points: VideoPoint[];
  currentMs: number;
  activeId?: string | null; // dot hover → card highlight
  hoverIds?: Set<string>;
  onHover?: (id: string | null) => void;
  onSeek: (ms: number, point: VideoPoint) => void;
  frameUrl?: (t: number) => string;
  onLeadStatus?: (leadId: string, status: string, reason?: string) => void;
  title?: string;
  emptyText?: string;
}

type StatusFilter = "open" | "all" | "confirmed" | "dismissed";

const PointsOfInterestList: React.FC<Props> = ({ points, currentMs, activeId, hoverIds, onHover, onSeek, frameUrl, onLeadStatus, title = "Points of interest", emptyText }) => {
  const [status, setStatus] = useState<StatusFilter>("open");
  const [kind, setKind] = useState("all");
  const [sort, setSort] = useState<"time" | "severity">("time");
  const listRef = useRef<HTMLUListElement>(null);
  const hasLeads = points.some((p) => p.source === "lead");

  const kinds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of points) counts.set(p.kind, (counts.get(p.kind) || 0) + 1);
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [points]);

  const visible = useMemo(() => {
    let out = points.filter((p) => kind === "all" || p.kind === kind);
    if (hasLeads) {
      out = out.filter((p) => {
        if (p.source !== "lead") return status !== "confirmed" && status !== "dismissed";
        if (status === "open") return p.status !== "dismissed";
        if (status === "all") return true;
        return p.status === status;
      });
    }
    return [...out].sort((a, b) => (sort === "severity" ? (b.severity || 0) - (a.severity || 0) || a.tStartMs - b.tStartMs : a.tStartMs - b.tStartMs));
  }, [points, kind, status, sort, hasLeads]);

  const active = useMemo(() => new Set(pointsAt(points, currentMs).map((p) => p.id)), [points, currentMs]);
  const lastScrolled = useRef<string | null>(null);
  useEffect(() => {
    const first = [...active][0];
    if (!first || first === lastScrolled.current) return;
    lastScrolled.current = first;
    scrollWithin(listRef.current, listRef.current?.querySelector<HTMLElement>(`[data-point="${CSS.escape(first)}"]`) || null);
  }, [active]);

  return (
    <div data-tour="footage-points" className="flex flex-col min-h-0 h-full">
      <div className="px-3 pt-3 pb-2 border-b border-base-300 space-y-2">
        <div className="flex items-center gap-2">
          <h3 className="font-semibold text-base-content text-sm">{title}</h3>
          <span className="text-xs text-base-content/50">{visible.length}</span>
          <div className="ml-auto inline-flex rounded-md border border-base-300 overflow-hidden text-xs">
            <Tooltip overlay="Sort by time">
              <button type="button" onClick={() => setSort("time")} className={cn("px-1.5 py-1 transition-colors", sort === "time" ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "text-base-content/50 hover:bg-base-200")}>
                <ClockIcon className="size-3.5" />
              </button>
            </Tooltip>
            <Tooltip overlay="Sort by severity">
              <button type="button" onClick={() => setSort("severity")} className={cn("px-1.5 py-1 border-l border-base-300 transition-colors", sort === "severity" ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "text-base-content/50 hover:bg-base-200")}>
                <FireIcon className="size-3.5" />
              </button>
            </Tooltip>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasLeads && (
            <div className="inline-flex rounded-md border border-base-300 overflow-hidden text-xs">
              {(["open", "confirmed", "dismissed", "all"] as StatusFilter[]).map((s, i) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStatus(s)}
                  className={cn("px-2 py-1 capitalize transition-colors", i > 0 && "border-l border-base-300", status === s ? "bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300" : "text-base-content/60 hover:bg-base-200")}
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          <div data-tour="footage-kind-filter">
          <Dropdown
            size="xs"
            dense
            contentClassName="w-52"
            options={[{ value: "all", label: "All kinds" }, ...kinds.map(([k, n]) => ({ value: k, label: `${kindLabel(k)} (${n})` }))]}
            value={kind}
            onChange={setKind}
          />
          </div>
        </div>
      </div>
      {visible.length === 0 && <p className="p-3 text-sm text-base-content/60">{emptyText || (points.length ? "Nothing matches these filters." : "Nothing flagged in this footage.")}</p>}
      <ul ref={listRef} key={`${status}|${kind}|${sort}`} className="flex-1 overflow-y-auto p-3 space-y-2">
        {visible.map((p, i) => {
          const style = kindStyle(p.kind);
          const lit = active.has(p.id) || activeId === p.id || hoverIds?.has(p.id);
          const lead = p.lead;
          return (
            <li key={p.id} data-point={p.id} data-tour="footage-point" style={revealProps(i).style} className="reveal">
              <div
                onClick={() => onSeek(p.tStartMs, p)}
                onMouseEnter={() => onHover?.(p.id)}
                onMouseLeave={() => onHover?.(null)}
                title="Jump to this moment"
                className={cn(
                  "rounded-lg border p-2 cursor-pointer transition-colors",
                  lit ? cn("shadow-sm", SEVERITY_ACTIVE_CLASSES[p.severity || 3]) : "border-base-300 bg-base-100 hover:border-blue-300 dark:hover:border-blue-800 hover:bg-blue-50/60 dark:hover:bg-blue-900/20",
                  p.status === "dismissed" && "opacity-50"
                )}
              >
                <div className="flex items-start gap-2">
                  {frameUrl && p.frameTs.length > 0 && (
                    <img src={frameUrl(p.frameTs[0])} alt="" loading="lazy" className="w-20 h-[45px] object-cover rounded shrink-0 bg-base-300" />
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className={cn("size-2.5 rounded-full shrink-0", style.dot)} />
                      {p.severity != null && <span className={cn("text-[11px] font-semibold px-1.5 rounded", SEVERITY_CLASSES[p.severity])}>sev {p.severity}</span>}
                      <span className="text-xs font-medium">{p.title}</span>
                      <button
                        className="ml-auto text-xs text-blue-600 dark:text-blue-300 hover:underline tabular-nums"
                        onClick={(e) => {
                          e.stopPropagation();
                          onSeek(p.tStartMs, p);
                        }}
                      >
                        ▶ {formatClock(p.tStartMs)}
                      </button>
                    </div>
                    <p className={cn("mt-1 text-[13px] leading-5 text-base-content/85", p.source === "lead" && "italic")}>
                      {p.source === "lead" ? `"${p.description}"` : p.description}
                    </p>
                    {p.textRead && <p className="text-xs text-base-content/70 mt-0.5">Read: <span className="font-mono">{p.textRead}</span></p>}
                    {p.explanation && <p className="text-xs text-base-content/60 mt-0.5">{p.explanation}</p>}
                    {p.confidence != null && p.source !== "lead" && <p className="text-[11px] text-base-content/40 mt-0.5">{Math.round(p.confidence * 100)}% confident</p>}
                  </div>
                </div>
                {lead && onLeadStatus && (
                  <div className="mt-2 flex items-center gap-1.5">
                    <TriageButton
                      kind="confirm"
                      active={lead.status === "confirmed"}
                      onClick={(e) => {
                        e.stopPropagation();
                        onLeadStatus(lead.id, lead.status === "confirmed" ? "new" : "confirmed");
                      }}
                    />
                    {lead.status === "dismissed" ? (
                      <TriageButton
                        kind="restore"
                        onClick={(e) => {
                          e.stopPropagation();
                          onLeadStatus(lead.id, "new");
                        }}
                      />
                    ) : (
                      <div className="dropdown dropdown-bottom" onClick={(e) => e.stopPropagation()}>
                        <TriageButton kind="dismiss" tabIndex={0} />
                        <ul tabIndex={0} className="dropdown-content menu bg-base-100 rounded-lg z-[30] w-56 p-2 shadow border border-base-300 mt-1">
                          {DISMISS_REASONS.map((r) => (
                            <li key={r.value}>
                              <button
                                className="flex flex-col items-start gap-0 hover:bg-gray-100 dark:hover:bg-base-200"
                                onClick={() => {
                                  onLeadStatus(lead.id, "dismissed", r.value);
                                  (document.activeElement as HTMLElement | null)?.blur();
                                }}
                              >
                                <span className="text-sm text-base-content">{r.label}</span>
                                <span className="text-[11px] text-base-content/50">{r.hint}</span>
                              </button>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PointsOfInterestList;
